import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logoutGarage, getGarageInfo } from "../../api/garageAuth";
import GarageProfile from "../GarageProfile";
import { C, VIEW_TITLES, INVENTORY_VIEWS, Sidebar, Dashboard, InventorySection, OpsSection, Reports, OPS, OutlineBtn, PrimaryBtn } from "./runtime";
import "../../styles/garage/garage-management.css";
function downloadCsv(name, rows) {
  if (!rows?.length) { alert("Nothing to export on this page yet."); return; }
  const cols = [...new Set(rows.flatMap(r => Object.keys(r).filter(k => r[k] === null || typeof r[k] !== "object")))];
  const esc  = v => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv  = [cols.join(","), ...rows.map(r => cols.map(c => esc(r[c])).join(","))].join("\n");
  const a    = document.createElement("a");
  a.href     = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── Root component ─────────────────────────────────────────────
// Reached only via <GarageRoute><GarageManagement/></GarageRoute> in App.jsx,
// so by the time this renders there's already a valid garage session.
export default function GarageManagement() {
  const navigate = useNavigate();
  const [view, setView] = useState("dashboard");
  const exportRef = useRef(null);
  const garage = getGarageInfo(); // { id, name } — display only

  const registerExport = useCallback((name, rows) => { exportRef.current = { name, rows }; }, []);

  // if the token expires mid-session, a 401 from any request sends the user
  // back to the garage login (never touches a superadmin session elsewhere)
  useEffect(() => {
    const onUnauthorized = () => {
      logoutGarage();
      navigate("/garage-login", { replace: true, state: { from: "/garage" } });
    };
    window.addEventListener("garage-unauthorized", onUnauthorized);
    return () => window.removeEventListener("garage-unauthorized", onUnauthorized);
  }, [navigate]);

  const changeView = (v) => { exportRef.current = null; setView(v); };

  const renderContent = () => {
    if (view === "dashboard") return <Dashboard />;
    if (view === "reports")   return <Reports key={view} registerExport={registerExport} />;
    if (INVENTORY_VIEWS.has(view)) return <InventorySection key={view} tab={view} registerExport={registerExport} />;
    if (view === "profile") return <GarageProfile colors={C} />;
    if (OPS[view])            return <OpsSection key={view} id={view} registerExport={registerExport} />;
    return null;
  };

  return (
    <div className="garage-shell">
      <Sidebar view={view} onView={changeView} onLogout={() => { logoutGarage(); navigate("/garage-login", { replace: true }); }} />

      <div className="garage-main">
        <header className="garage-topbar">
          <div className="garage-page-title">
            <div className="garage-breadcrumb"><i className="fa-solid fa-layer-group" aria-hidden="true" /> {garage?.name || "My Car Kenya"}</div>
            <div className="garage-title-row">
              <h1>{VIEW_TITLES[view] || view}</h1>
            </div>
          </div>
          <div className="garage-top-actions">
            {view === "reports"
              ? <OutlineBtn onClick={() => window.print()}><i className="fa-solid fa-file-pdf" aria-hidden="true" /> Save as PDF</OutlineBtn>
              : view !== "dashboard" && <OutlineBtn onClick={() => exportRef.current && downloadCsv(exportRef.current.name, exportRef.current.rows)}><i className="fa-solid fa-download" aria-hidden="true" /> Export CSV</OutlineBtn>}
          </div>
        </header>

        <main className="garage-content">{renderContent()}</main>
      </div>
    </div>
  );
}
